import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';


import { variables, helpers } from './..';
import { amplifyCognito, api } from './../services';
import { useOpenConfig } from 'utilities/variables/openConfigContext'; 


export enum AuthStatus {
  Loading,
  SignedIn,
  SignedOut,
}



export interface IAuth {
  sessionInfo?: { username?: string; identifier?: string; sub?: string; accessToken?: string; refreshToken?: string },
  userProfile?: any
  attrInfo?: any
  authStatus?: AuthStatus
  signInEmailOnly?: any
  unifiedSignIn?: any
  signInUsernamePassword?: any
  signUpWithEmail?: any
  signOut?: any
  verifyCode?: any
  getSession?: any
  refreshSessionInfo?: any
  sendCode?: any
  forgotPassword?: any
  changePassword?: any
  getAttributes?: any
  setAttribute?: any
  getApiSessionTokens?: any
  getAmplifyCurrentAthenticatedUser?: any,
  authFlow: variables.DEFAULT_AUTH_FLOWS.EMAIL_ONLY | variables.DEFAULT_AUTH_FLOWS.EMAIL_PASSWORD | variables.DEFAULT_AUTH_FLOWS.EMAIL_STRAT | variables.DEFAULT_AUTH_FLOWS.PASSWORD_MFA_STRAT | variables.DEFAULT_AUTH_FLOWS.UNIFIED_AUTH,
  // userKeys?: bsgCrypto.CryptoData
}

// Create a minimal default if needed (no config used yet)
const defaultState: IAuth = {
  authStatus: AuthStatus.Loading,
  sessionInfo: {},
  userProfile: {},
  attrInfo: [],
  authFlow: variables.DEFAULT_AUTH_FLOWS.UNIFIED_AUTH, // a fallback if you want
};

type Props = {
  children?: React.ReactNode
}

export const AuthContext = React.createContext<IAuth>(defaultState);

export const AuthIsSignedIn = ({ children }: Props) => {
  const { authStatus } = useContext(AuthContext);

  return <>{authStatus === AuthStatus.SignedIn ? children : null}</>
}

export const EnsureSignedIn = ({ children }: Props) => {
  const { authStatus } = useContext(AuthContext);
  const location = useLocation();
  // const navigate = useNavigate()

  // Add basket state to router
  // Add ability to store and retreive from local storage
  // Add a 'Basket Provider Wrapper'
  return <>{
    authStatus === AuthStatus.SignedIn ? 
      children : 
    // navigate("/signin", { state: {from: location.pathname}})
      <Navigate 
        to="/authenticate" 
        state={{
          from: location.pathname,
          currentBasket: {}
        }} 
        replace 
      />
  }</>
}

export const EnsureAdminSignedIn = ({ children }: Props) => {
  const { authStatus, userProfile } = useContext(AuthContext);
  const location = useLocation();
  // const navigate = useNavigate()

  // Add basket state to router
  // Add ability to store and retreive from local storage
  // Add a 'Basket Provider Wrapper'
  return (
    <>
      {
        authStatus === AuthStatus.SignedIn &&
        userProfile && 
        [variables.DEFAULT_USER_ROLES.ADMINISTRATOR, variables.DEFAULT_USER_ROLES.SUPERADMINISTRATOR].includes(userProfile.role)
          ? children
          : <Navigate 
            to="/authenticate" 
            state={{
              from: location.pathname,
              currentBasket: {}
            }} 
            replace 
          />
      }
    </>
  );
}

export const AuthIsNotSignedIn = ({ children }: Props) => {
  const { authStatus } = useContext(AuthContext);

  return <>{authStatus === AuthStatus.SignedOut ? children : null}</>
}

export const AuthProvider = ({ children }: Props) => {

  const { config } = useOpenConfig();
  const openFlowStr = config?.AUTHENTICATION_FLOW || variables.DEFAULT_AUTH_FLOWS.UNIFIED_AUTH;
  let openFlowEnum = variables.DEFAULT_AUTH_FLOWS.UNIFIED_AUTH; 

  if (openFlowStr === 'CUSTOM_AUTH') {
    openFlowEnum = variables.DEFAULT_AUTH_FLOWS.UNIFIED_AUTH; // same underlying string, but TS sees the enum
  }

  const [authStatus, setAuthStatus] = useState(AuthStatus.Loading)
  const [sessionInfo, setSessionInfo] = useState<any>({})
  const [userProfile, setUserProfile] = useState<any>({})
  const [attrInfo, setAttrInfo] = useState([])
  // const [userKeys, setUserKeys] = useState<bsgCrypto.CryptoData|undefined>(undefined)

  const navigate = useNavigate();
  const location = useLocation();
  

  const getSessionInfo = useCallback(async () => {
    try {
      const session: any = await amplifyCognito.getCurrSession();

      window.localStorage.setItem('accessToken', session.accessToken.jwtToken);
      window.localStorage.setItem('refreshToken', session.refreshToken.token);

      const currentAuthenticatedUser: any = await amplifyCognito.getAmplifyCurrentAthenticatedUser();
      const currentUserAttributesResponse: any = await getProfileAttributes(currentAuthenticatedUser.attributes.sub);
      const currentUserRole: any = await getCurrentUserRole();

      setSessionInfo({
        accessToken: session.accessToken.jwtToken,
        refreshToken: session.refreshToken.token,
        idToken: session.idToken.jwtToken,
      });

      setUserProfile({
        authToken: session.idToken.jwtToken,
        username: currentAuthenticatedUser.username,
        identifier: currentUserAttributesResponse.identifier,
        attributes: {
          ...currentAuthenticatedUser.attributes,
          firstName: currentUserAttributesResponse.name.first,
          lastName: currentUserAttributesResponse.name.last,
          middleName: currentUserAttributesResponse.name.middle,
          displayName: currentUserAttributesResponse.display_name,
          phone: currentUserAttributesResponse.phone,
          identifier: currentUserAttributesResponse.identifier,
        },
        role: currentUserRole,
      });

      setAuthStatus(AuthStatus.SignedIn);
    } catch (err) {
      setAuthStatus(AuthStatus.SignedOut);
    }
  }, [setSessionInfo, setUserProfile, setAuthStatus]);


  const checkFromSignInRedirect = useCallback(() => {
    if (location.pathname === '/authenticate' && authStatus === AuthStatus.SignedIn) {
      const redirectPath = location?.state?.from;
  
      if (redirectPath) {
        navigate(redirectPath);
      }
    }
  }, [location, navigate, authStatus]);

  useEffect(() => {

    getSessionInfo();
    checkFromSignInRedirect();
    
  }, [getSessionInfo, checkFromSignInRedirect])


  const getCurrentUserRole = async() => {
    const usersData = await api.getUsers({});
    const cuid: any =  await amplifyCognito.getAmplifyCurrentAthenticatedUser()
    return usersData.body && usersData.body.find((u: any) => u.id ===  cuid['attributes']['sub'])['role'];
  }

  const getProfileAttributes = async (userSub: string) => {
    try {
      const userAtrributesResponse = await api.getUserAttributes({
        body: {
          id: userSub
        }
      });

      if ( userAtrributesResponse.status === variables.DEFAULT_API_RESULT.SUCCESS){
        
        return userAtrributesResponse.body;
      } else {

        /**
         * TO-DO:
         * - DO some error pop-up here for eg.
         */
        helpers.logToOutput('User Profile Attributes: Err: ', userAtrributesResponse.errorMessage);
      }
    } catch (err) {
      throw err
    }
    
      
    
  }

  const checkFromSignOutRedirect = () => {
    // if (location.pathname === '/signin' && authStatus === AuthStatus.SignedIn){
    //   if (location.state.from){
    //     navigate(location.state.from);
    //   }
    // } 
    navigate('/')
  }

  if (authStatus === AuthStatus.Loading) {
    return null
  }

  

  const signInEmailOnly = async(identifier: string) => {
    const setStatusToTrue = () => {
      setAuthStatus(AuthStatus.SignedIn);
    }
    
    try {
      const responseUser = await amplifyCognito.signInEmailOnly(setStatusToTrue, identifier);
      return responseUser;
      
    } catch (err) {
      helpers.logToOutput('Error during authentication or key gen', err)
    }
  }


  const unifiedSignIn = async(identifier: string) => {
    const setStatusToTrue = () => {
      setAuthStatus(AuthStatus.SignedIn);
    }
    
    try {
      const responseUser = await amplifyCognito.unifiedAuth(setStatusToTrue, identifier);
      return responseUser;
      
    } catch (err) {
      helpers.logToOutput('Error during authentication or key gen', err)
    }
  }

  const signInUsernamePassword = async(username: string, password?: string, newPassword?: string, userMfaCode?: string, setMfaSetupCode?: string) => {
    try {
      await amplifyCognito.signInWithUsernamePassword(username, password, newPassword, userMfaCode, setMfaSetupCode);
      setAuthStatus(AuthStatus.SignedIn);
    } catch (err) {
      setAuthStatus(AuthStatus.SignedOut)
      throw err
    }
  }

  const signUpWithEmail = async(identifier: string) => {
    return await amplifyCognito.signUpUserWithEmail(identifier)
  }

  const signOut = async() => {
    await amplifyCognito.signOut();

    setAuthStatus(AuthStatus.SignedOut);
    setSessionInfo({})
    setUserProfile({})
    setAttrInfo([])

    window.localStorage.setItem('accessToken', '');
    window.localStorage.setItem('refreshToken', '');

    // navigate("/")
    checkFromSignOutRedirect();
  }

  const getSession = async() => {
    try {
      const session: any = await amplifyCognito.getCurrSession();
      
      window.localStorage.setItem('accessToken', `${session.accessToken.jwtToken}`)
      window.localStorage.setItem('refreshToken', `${session.refreshToken.token}`)

      return session
    } catch (err) {
      throw err
    }
  }

  const getApiSessionTokens = async() => {
    try {
      const session: any = await amplifyCognito.getCurrSession()
      return {
        authToken: session.idToken.jwtToken,
        username: session.idToken.payload['cognito:username']
      }
    } catch (err) {
      throw err
    }
  }

  const forgotPassword = async(username: string, code: string, newPassword?: string) => {
    try {
      await amplifyCognito.forgotPassword(username, code, newPassword)
    } catch (err) {
      throw err
    }
  }

  const changePassword = async(oldPassword: string, newPassword: string) => {
    try {
      await amplifyCognito.changePassword(oldPassword, newPassword)
    } catch (err) {
      throw err
    }
  }

  const refreshSessionInfo = async() => {
    try {
      const session: any = await getSession()

      setSessionInfo({
        accessToken: session.accessToken.jwtToken,
        refreshToken: session.refreshToken.token,
      })
      
      setUserProfile({
        authToken: session.idToken.jwtToken,
        username: session.idToken.payload['cognito:username'] 
      })
      
      window.localStorage.setItem('accessToken', `${session.accessToken.jwtToken}`)
      window.localStorage.setItem('refreshToken', `${session.refreshToken.token}`)
      // await setAttribute({ Name: 'website', Value: 'https://kuroweb.bsg-dev.com' })
      setAuthStatus(AuthStatus.SignedIn)
    } catch (err) {
      setAuthStatus(AuthStatus.SignedOut)

      // navigate("/")
    }
  }

  const getAmplifyCurrentAthenticatedUser = async() => {
    try {
      return await amplifyCognito.getAmplifyCurrentAthenticatedUser()
    } catch (err) {
      throw err
    }
  }

  


  const state: IAuth = {
    authStatus,
    sessionInfo,
    attrInfo,
    userProfile,
    //authFlow: defaultState.authFlow,
    authFlow: openFlowEnum,
    // userKeys,
    signUpWithEmail,
    signInEmailOnly,
    unifiedSignIn,
    signInUsernamePassword,
    signOut,
    getSession,
    refreshSessionInfo,
    forgotPassword,
    changePassword,
    getApiSessionTokens,
    getAmplifyCurrentAthenticatedUser,
  }

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

